import { Button, Upload } from 'antd';
import React, { ReactElement } from 'react';
import { UploadChangeParam, UploadFile, UploadProps } from 'antd/lib/upload/interface';
import { UploadOutlined } from '@ant-design/icons';
import { centeredNotification } from '../../utils/utility';
import { APPLICATION_ID_2, COMMON_ERROR, DEFAULT_MAX_FILE_SIZE, MAX_FILES, NO_FILE_TEXT } from '../../shared/constants/commonConstants';
import { useDispatch } from 'react-redux';
import { ReactComponent as DownloadIcon } from '../../assets/images/svg/Download-icon.svg';
import { submitAdmission } from '../../store/actions/fileAction';
import { fileCountValidation, getMaxFileSizeMessage } from '../crm/inPatient/service/crmUtils';
import { MAX_FILE_LIMIT_COUNT, UPLOAD_STATUS } from '../../shared/constants/crmConstants';
import { GrievanceFile } from '../../shared/interfaces/grievanceInterfaces';
import { isEmpty } from 'lodash';
import { GLOBAL_URL } from '../../shared/constants/dataServiceUrl';

const UploadFiles = (props: any): ReactElement => {
  const { fileList, setFileList, grievanceTransactions, showUploadIcon, newGrievance, fileListRef } = props;
  const dispatch = useDispatch();

  const downloadFile = (docPath: string): void => {
    if (docPath) {
      dispatch(submitAdmission(docPath));
    } else {
      centeredNotification(NO_FILE_TEXT);
    }
  };

  const downloadAllFiles = (): void => {
    if (!isEmpty(grievanceTransactions)) {
      grievanceTransactions?.forEach((file: GrievanceFile): void => {
        downloadFile(file?.docPath);
      });
    } else {
      centeredNotification(NO_FILE_TEXT);
    }
  };

  const fileBeforeUpload = (file: File, fileListItem: UploadFile<any>[]): boolean => {
    const totalFiles = fileListRef?.current?.length + fileListItem?.length; // Count new + existing files
    if (file.size > DEFAULT_MAX_FILE_SIZE) { // Check if file is larger than 15MB
      centeredNotification(getMaxFileSizeMessage(MAX_FILE_LIMIT_COUNT));
      return false;
    }
    if (totalFiles > MAX_FILES) {
      centeredNotification(fileCountValidation(MAX_FILES));
      return true;
    }
    return true;
  };

  const draggerActions: UploadProps = {
    showUploadList: true,
    multiple: true,
    action: GLOBAL_URL.CRM_BASE_URL + 'Provider/File/Upload',
    // headers: { accessToken: ApplicationDetail.instance.getAccessToken() },
    withCredentials: true,
    beforeUpload: (file: any, incomingFiles: any): boolean | Promise<void> => fileBeforeUpload(file, incomingFiles),
    onChange: async (info: UploadChangeParam<UploadFile<any>>): Promise<void> => {
      let { fileList: updatedFileList } = info;
      const { file } = info;
      const { status: fileStatus } = file;
      const { isSuccess, errorMessage } = file?.response || {};
      // Early return if file upload has an error
      if (fileStatus === UPLOAD_STATUS.error) {
        centeredNotification(COMMON_ERROR, 'error');
        return;
      }
      // Handle successful file upload
      if (fileStatus === UPLOAD_STATUS.done) {
        if (isSuccess) {
          // Filter out files larger than 15MB and limit total count
          updatedFileList = updatedFileList
            ?.filter((f: any): boolean => f?.size <= DEFAULT_MAX_FILE_SIZE) // Filter files larger than 15MB
            ?.slice(0, MAX_FILES); // Limit total count
          setFileList(updatedFileList);
          fileListRef.current = updatedFileList;
        } else {
          // Remove the failed file from the list and show error notification
          centeredNotification(errorMessage || COMMON_ERROR, 'error');
          setFileList((prevList: any): any => prevList?.filter((f: any): boolean => f.uid !== file.uid));
        }
      }
    },
    fileList,
    data: (): Record<string, string> => ({
      FileUploadType: 'Report',
      ApplicationId: APPLICATION_ID_2,
    }),
  };

  return (
    <>
      {showUploadIcon ? (
        newGrievance ? (
          <Upload {...draggerActions} itemRender={(): any => null}>
            <Button className="grievance-new-upload-file">
              <b>Click to Upload</b>
            </Button>
          </Upload>
        ) : (
          <Upload {...draggerActions} itemRender={(): any => null}>
            <UploadOutlined className="grievance-new-upload-files" />
          </Upload>
        )
      ) : (
        <DownloadIcon onClick={downloadAllFiles} className="grievance-upload-file" />
      )}
    </>
  );
};

export default UploadFiles;
